import type {EstablishedAhpSession} from '@github-ui/ahp-session-client/contracts'
import {useEffect, useEffectEvent, useRef} from 'react'

interface AwaitingIntentWakeOptions {
  identityKey: string | undefined
  awaitingIntent: boolean
  session: {commands: Pick<EstablishedAhpSession['commands'], 'ensureLive'>} | undefined
  environmentStatus: string | undefined
  refetchEnvironment?: () => Promise<string | undefined>
}

export function useAwaitingIntentWake({
  identityKey,
  awaitingIntent,
  session,
  environmentStatus,
  refetchEnvironment,
}: AwaitingIntentWakeOptions): void {
  const wakeRequestedRef = useRef(false)
  const previousRef = useRef({identityKey, awaitingIntent, environmentStatus})
  const hasSession = Boolean(session)

  const wake = useEffectEvent(() => {
    if (wakeRequestedRef.current || !session?.commands.ensureLive) return
    wakeRequestedRef.current = true
    void session.commands.ensureLive()
  })

  const checkEnvironment = useEffectEvent(async (isCurrent: () => boolean) => {
    if (document.visibilityState !== 'visible' || !navigator.onLine || !refetchEnvironment) return
    const status = await refetchEnvironment()
    if (isCurrent() && document.visibilityState === 'visible' && navigator.onLine && status === 'online') wake()
  })

  useEffect(() => {
    wakeRequestedRef.current = false
  }, [identityKey, awaitingIntent])

  useEffect(() => {
    if (!awaitingIntent) return
    let current = true
    const onRecovery = () => void checkEnvironment(() => current)
    window.addEventListener('online', onRecovery)
    document.addEventListener('visibilitychange', onRecovery)
    onRecovery()
    return () => {
      current = false
      window.removeEventListener('online', onRecovery)
      document.removeEventListener('visibilitychange', onRecovery)
    }
  }, [identityKey, awaitingIntent, hasSession])

  useEffect(() => {
    const previous = previousRef.current
    previousRef.current = {identityKey, awaitingIntent, environmentStatus}
    if (
      previous.identityKey === identityKey &&
      previous.awaitingIntent &&
      awaitingIntent &&
      previous.environmentStatus !== 'online' &&
      environmentStatus === 'online' &&
      document.visibilityState === 'visible'
    ) {
      wake()
    }
  }, [identityKey, awaitingIntent, environmentStatus])
}
